/**
 * `expo-image-manipulator` no browser (canvas). Suporta a API clássica
 * (`manipulateAsync`) e a nova (`ImageManipulator.manipulate(...).renderAsync()`).
 * Saída gravada no FS virtual (cache/ImageManipulator).
 */
import { bytesParaBase64, gravarBlobNoCache, uriParaBlob } from "@/platform/file-system";

export enum SaveFormat {
  JPEG = "jpeg",
  PNG = "png",
  WEBP = "webp",
}

export enum FlipType {
  Vertical = "vertical",
  Horizontal = "horizontal",
}

export type Action =
  | { resize: { width?: number; height?: number } }
  | { rotate: number }
  | { flip: FlipType }
  | { crop: { originX: number; originY: number; width: number; height: number } };

export type SaveOptions = { compress?: number; format?: SaveFormat; base64?: boolean };
export type ImageResult = { uri: string; width: number; height: number; base64?: string };

type Tela = HTMLCanvasElement | OffscreenCanvas;

function novaTela(w: number, h: number): Tela {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

function ctx2d(t: Tela) {
  return t.getContext("2d") as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
}

async function decodificar(uri: string): Promise<Tela> {
  const blob = await uriParaBlob(uri);
  const bmp = await createImageBitmap(blob, { imageOrientation: "from-image" } as ImageBitmapOptions);
  const t = novaTela(bmp.width, bmp.height);
  ctx2d(t).drawImage(bmp, 0, 0);
  bmp.close();
  return t;
}

function aplicar(t: Tela, a: Action): Tela {
  if ("resize" in a) {
    const { width, height } = a.resize;
    const w = width ?? Math.round(((height ?? t.height) * t.width) / t.height);
    const h = height ?? Math.round(((width ?? t.width) * t.height) / t.width);
    const n = novaTela(Math.max(1, Math.round(w)), Math.max(1, Math.round(h)));
    const c = ctx2d(n);
    c.imageSmoothingQuality = "high";
    c.drawImage(t as CanvasImageSource, 0, 0, n.width, n.height);
    return n;
  }
  if ("rotate" in a) {
    const graus = ((a.rotate % 360) + 360) % 360;
    const rad = (graus * Math.PI) / 180;
    const troca = graus === 90 || graus === 270;
    const n = novaTela(troca ? t.height : t.width, troca ? t.width : t.height);
    const c = ctx2d(n);
    c.translate(n.width / 2, n.height / 2);
    c.rotate(rad);
    c.drawImage(t as CanvasImageSource, -t.width / 2, -t.height / 2);
    return n;
  }
  if ("flip" in a) {
    const n = novaTela(t.width, t.height);
    const c = ctx2d(n);
    if (a.flip === FlipType.Horizontal) {
      c.translate(t.width, 0);
      c.scale(-1, 1);
    } else {
      c.translate(0, t.height);
      c.scale(1, -1);
    }
    c.drawImage(t as CanvasImageSource, 0, 0);
    return n;
  }
  const { originX, originY, width, height } = a.crop;
  const n = novaTela(Math.max(1, Math.round(width)), Math.max(1, Math.round(height)));
  ctx2d(n).drawImage(t as CanvasImageSource, originX, originY, width, height, 0, 0, n.width, n.height);
  return n;
}

async function paraBlob(t: Tela, formato: SaveFormat, qualidade: number): Promise<Blob> {
  const mime = `image/${formato}`;
  if ("convertToBlob" in t) return t.convertToBlob({ type: mime, quality: qualidade });
  return new Promise((ok, falha) => (t as HTMLCanvasElement).toBlob((b) => (b ? ok(b) : falha(new Error("Falha ao gerar a imagem"))), mime, qualidade));
}

async function salvar(t: Tela, opts: SaveOptions = {}): Promise<ImageResult> {
  const formato = opts.format ?? SaveFormat.JPEG;
  const blob = await paraBlob(t, formato, opts.compress ?? 1);
  const uri = await gravarBlobNoCache(blob, formato === SaveFormat.JPEG ? "jpg" : formato, "ImageManipulator");
  const out: ImageResult = { uri, width: t.width, height: t.height };
  if (opts.base64) out.base64 = bytesParaBase64(new Uint8Array(await blob.arrayBuffer()));
  return out;
}

export async function manipulateAsync(uri: string, actions: Action[] = [], saveOptions: SaveOptions = {}): Promise<ImageResult> {
  let t = await decodificar(uri);
  for (const a of actions) t = aplicar(t, a);
  return salvar(t, saveOptions);
}

// ─── API nova ───────────────────────────────────────────────────────────────────

export class ImageRef {
  constructor(private tela: Tela) {}
  get width() {
    return this.tela.width;
  }
  get height() {
    return this.tela.height;
  }
  saveAsync(opts: SaveOptions = {}) {
    return salvar(this.tela, opts);
  }
  release() {}
  /** @internal */
  __tela() {
    return this.tela;
  }
}

class Contexto {
  private acoes: Action[] = [];
  constructor(private fonte: string | ImageRef) {}
  resize(s: { width?: number | null; height?: number | null }) {
    this.acoes.push({ resize: { width: s.width ?? undefined, height: s.height ?? undefined } });
    return this;
  }
  rotate(graus: number) {
    this.acoes.push({ rotate: graus });
    return this;
  }
  flip(f: FlipType | "horizontal" | "vertical") {
    this.acoes.push({ flip: f as FlipType });
    return this;
  }
  crop(r: { originX: number; originY: number; width: number; height: number }) {
    this.acoes.push({ crop: r });
    return this;
  }
  extent() {
    return this;
  }
  reset() {
    this.acoes = [];
    return this;
  }
  async renderAsync(): Promise<ImageRef> {
    let t = typeof this.fonte === "string" ? await decodificar(this.fonte) : this.fonte.__tela();
    for (const a of this.acoes) t = aplicar(t, a);
    return new ImageRef(t);
  }
  release() {}
}

export const ImageManipulator = {
  manipulate: (fonte: string | ImageRef) => new Contexto(fonte),
};

export function useImageManipulator(fonte: string) {
  return new Contexto(fonte);
}
